/**
 * @description
 * Payment settings component for managing payment methods and customer portal access.
 * Provides links to Stripe Customer Portal for payment method management.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/PaymentSettings.test.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface PaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    last4: string;
    exp_month: number;
    exp_year: number;
  };
  isDefault: boolean;
}

interface PaymentSettingsProps {
  clientId: string;
}

interface StripeCustomer {
  stripeCustomerId: string;
  email: string;
  currency: string;
  [key: string]: unknown;
}

/**
 * Payment settings component
 */
export function PaymentSettings({ clientId }: PaymentSettingsProps) {
  const [customer, setCustomer] = useState<StripeCustomer | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);

  const loadPaymentData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get Stripe customer
      const customerData = await api.getStripeCustomer(clientId);
      setCustomer(customerData);

      if (customerData) {
        // Get real payment methods from Stripe API
        const paymentMethodsData = await api.getPaymentMethods(customerData.stripeCustomerId);
        setPaymentMethods(paymentMethodsData);
      }
    } catch (error) {
      console.error('Error loading payment data:', error);
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadPaymentData();
  }, [loadPaymentData]);

  const handleOpenCustomerPortal = async () => {
    if (!customer) {
      toast.error('No Stripe customer found');
      return;
    }

    try {
      setPortalLoading(true);
      
      const portalData = await api.getCustomerPortalUrl(
        customer.stripeCustomerId,
        `${window.location.origin}/clients/${clientId}?tab=billing`
      );

      // Redirect to Stripe Customer Portal
      window.open(portalData.url, '_blank');
    } catch (error) {
      console.error('Error opening customer portal:', error);
      toast.error('Failed to open customer portal');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const getCardIcon = (brand: string) => {
    switch (brand.toLowerCase()) {
      case 'visa':
        return '💳';
      case 'mastercard':
        return '💳';
      case 'amex':
        return '💳';
      case 'discover':
        return '💳';
      default:
        return '💳';
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="text-center py-8">
          <svg className="h-12 w-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <h4 className="text-lg font-medium mb-2">No Stripe Customer</h4>
          <p className="text-sm text-muted-foreground">
            This client needs to be linked to a Stripe customer before payment settings can be managed.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Payment Settings</h3>
        <button
          onClick={handleOpenCustomerPortal}
          disabled={portalLoading}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          {portalLoading ? (
            <span className="flex items-center gap-2">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              Opening...
            </span>
          ) : (
            'Manage Payment Methods'
          )}
        </button>
      </div>

      {/* Customer Info */}
      <div className="mb-6 p-4 border rounded-md bg-muted/50">
        <h4 className="text-sm font-medium mb-2">Customer Information</h4>
        <div className="grid gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Customer ID:</span>
            <span className="font-mono">{customer.stripeCustomerId}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Email:</span>
            <span>{customer.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Currency:</span>
            <span className="uppercase">{customer.currency}</span>
          </div>
        </div>
      </div>

      {/* Payment Methods */}
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3">Payment Methods</h4>
        {paymentMethods.length > 0 ? (
          <div className="space-y-3">
            {paymentMethods.map((method) => (
              <div key={method.id} className="flex items-center justify-between p-3 border rounded-md">
                <div className="flex items-center gap-3">
                  <span className="text-lg">{getCardIcon(method.card?.brand || 'card')}</span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {method.card ? `${formatCardBrand(method.card.brand)} •••• ${method.card.last4}` : 'Payment Method'}
                      </span>
                      {method.isDefault && (
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          Default
                        </span>
                      )}
                    </div>
                    {method.card && (
                      <div className="text-sm text-muted-foreground">
                        Expires {method.card.exp_month.toString().padStart(2, '0')}/{method.card.exp_year}
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-sm text-muted-foreground">
                  {method.type}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 border rounded-md bg-muted/50">
            <svg className="h-8 w-8 text-muted-foreground mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
            </svg>
            <p className="text-sm text-muted-foreground">No payment methods found</p>
          </div>
        )}
      </div>

      {/* Customer Portal Info */}
      <div className="p-4 border rounded-md bg-blue-50">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Customer Portal</h4>
        <p className="text-sm text-blue-800 mb-3">
          The Stripe Customer Portal allows clients to manage their own payment methods, view invoices, and update billing information securely.
        </p>
        <div className="text-sm text-blue-700 space-y-1">
          <p>• Add, update, or remove payment methods</p>
          <p>• View and download invoices</p>
          <p>• Update billing address and contact information</p>
          <p>• Manage subscription settings</p>
        </div>
      </div>

      {/* Security Notice */}
      <div className="mt-4 p-3 border rounded-md bg-yellow-50">
        <div className="flex items-start gap-2">
          <svg className="h-4 w-4 text-yellow-600 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <div className="text-sm text-yellow-800">
            <p className="font-medium">Security Notice</p>
            <p>Payment methods are managed securely through Stripe's Customer Portal. We never store or have access to your payment card details.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
